import { useState, type KeyboardEvent } from 'react'
import { Link } from 'react-router-dom'
import { hhmm, parseWhen } from '../../../lib/useFictionalClock'
import styles from './Instruments.module.css'
import { useT } from '../../../lib/i18n'

export interface Plan {
  minutes: number
  from: Date
}

/** One inline-edit line: tap to open, Enter commits, Escape or blur closes. */
function Editable({
  className,
  display,
  initial,
  placeholder,
  onCommit,
  disabled,
}: {
  className: string
  display: React.ReactNode
  initial?: string
  placeholder: string
  onCommit: (value: string) => void
  disabled?: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(initial ?? '')

  if (!editing) {
    return (
      <div
        className={className}
        onClick={() => {
          if (disabled) return
          setValue(initial ?? '')
          setEditing(true)
        }}
      >
        {display}
      </div>
    )
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') return setEditing(false)
    if (e.key !== 'Enter') return
    onCommit(value.trim())
    setEditing(false)
  }

  return (
    <div className={className}>
      <input
        className={styles.input}
        value={value}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => setEditing(false)}
        autoFocus
      />
    </div>
  )
}

export default function Instruments({
  station,
  now,
  arrangement,
  onArrange,
  nextCall,
  onSetNextCall,
  plan,
  onSetPlan,
  onJump,
  onSignOff,
  jumpBusy,
  skedBusy,
  skedLabel,
  disabled,
}: {
  station: string
  now: Date | null
  arrangement: string | null
  onArrange: (text: string | null) => void
  nextCall: Date | null
  onSetNextCall: (d: Date | null) => void
  plan: Plan | null
  onSetPlan: (p: Plan | null) => void
  onJump: () => void
  onSignOff: () => void
  jumpBusy: boolean
  /** The pack's own sked, e.g. "— sign off until the morning sked —". */
  skedLabel: string
  skedBusy: boolean
  disabled?: boolean
}) {
  return (
    <header className={styles.header}>
      <Link to="/" className={styles.back}>
        ‹
      </Link>

      <div className={styles.station}>{station}</div>

      <div className={styles.right}>
        <div className={styles.clock}>{hhmm(now)}</div>
        <Book
          now={now}
          arrangement={arrangement}
          onArrange={onArrange}
          nextCall={nextCall}
          onSetNextCall={onSetNextCall}
          plan={plan}
          onSetPlan={onSetPlan}
          onJump={onJump}
          onSignOff={onSignOff}
          jumpBusy={jumpBusy}
          skedBusy={skedBusy}
          skedLabel={skedLabel}
          disabled={disabled}
        />
      </div>
    </header>
  )
}

/**
 * THE OPERATOR'S BOOK — the fields and the two acts, without the chrome around
 * them. Extracted so the phone can put the same book somewhere else entirely
 * (a sheet you pull up) rather than reproduce it: on a desk the book lies open
 * beside the transcript; in a hand there is only room for one of them at a time,
 * and the transcript is the one you came for.
 *
 * Everything here is the OPERATOR'S OWN note-taking. The engine parses none of
 * it, which is why the turnaround can read OVERDUE while he is perfectly fine.
 */
export function Book({
  now,
  arrangement,
  onArrange,
  nextCall,
  onSetNextCall,
  plan,
  onSetPlan,
  onJump,
  onSignOff,
  jumpBusy,
  skedBusy,
  skedLabel,
  disabled,
}: {
  now: Date | null
  arrangement: string | null
  onArrange: (text: string | null) => void
  nextCall: Date | null
  onSetNextCall: (d: Date | null) => void
  plan: Plan | null
  onSetPlan: (p: Plan | null) => void
  onJump: () => void
  onSignOff: () => void
  jumpBusy: boolean
  skedLabel: string
  skedBusy: boolean
  disabled?: boolean
}) {
  const t = useT()
  const minsToCall =
    nextCall && now ? Math.round((nextCall.getTime() - now.getTime()) / 60000) : null

  const nextCallDisplay = nextCall ? (
    <>
      next call <span className={styles.value}>{hhmm(nextCall)}</span>
      {minsToCall !== null && (minsToCall > 0 ? ` · in ${minsToCall}m` : ' · due')}
    </>
  ) : (
    '— next call — tap to set —'
  )

  let planDisplay: React.ReactNode = '— turnaround — tap to set —'
  if (plan && now) {
    const elapsed = Math.round((now.getTime() - plan.from.getTime()) / 60000)
    planDisplay =
      elapsed > plan.minutes ? (
        <>
          plan {plan.minutes} ·{' '}
          <span className={styles.over}>OVERDUE +{elapsed - plan.minutes}</span>
        </>
      ) : (
        <>
          plan {plan.minutes} · elapsed {elapsed}
        </>
      )
  }

  return (
    <>
        {/*
          The arrangement — the standing intent, in the player's own words. It is
          what he works at between contacts, and under continuationRequiresIntent
          an empty one means the world stops costing.
        */}
        <Editable
          className={`${styles.arrangement} ${arrangement ? styles.arrangementSet : ''}`}
          display={arrangement || t('desk.arrangement.unset')}
          initial={arrangement ?? ''}
          placeholder={t('desk.arrangement.placeholder')}
          onCommit={(v) => onArrange(v.length ? v : null)}
          disabled={disabled}
        />

        <div
          className={`${styles.action} ${skedBusy || disabled ? styles.busy : ''}`}
          onClick={() => !skedBusy && !disabled && onSignOff()}
        >
          {skedBusy ? t('desk.hoursPass') : skedLabel}
        </div>

        <Editable
          className={styles.field}
          display={nextCallDisplay}
          placeholder={t('desk.nextCall.placeholder')}
          onCommit={(v) =>
            onSetNextCall(v.length === 0 ? null : (parseWhen(v, now) ?? nextCall))
          }
          disabled={disabled}
        />

        <Editable
          className={styles.field}
          display={planDisplay}
          initial={plan ? String(plan.minutes) : ''}
          placeholder={t('desk.minutes')}
          onCommit={(v) => {
            const n = parseInt(v, 10)
            onSetPlan(v.length === 0 || !(n > 0) || !now ? null : { minutes: n, from: now })
          }}
          disabled={disabled}
        />

        <div
          className={`${styles.action} ${!nextCall || jumpBusy || disabled ? styles.off : ''}`}
          onClick={() => nextCall && !jumpBusy && !disabled && onJump()}
        >
          {jumpBusy ? '— the desk waits —' : '— jump to his next call —'}
        </div>
    </>
  )
}
